/**
 * FikoMassInfo - Mesh Hacim, Alan ve Kütle Merkezi Hesaplama Servisi
 *
 * Özellikler:
 * - Hacim hesabı (Signed Tetrahedron Volume)
 * - Yüzey alanı hesabı
 * - Kütle merkezi (Center of Mass / Centroid)
 * - Ağırlık hesabı (Hacim × Yoğunluk)
 *
 * @author FikoYT
 * @version 1.0.0
 */

import * as THREE from 'three';

/** Malzeme tanımı */
export interface Malzeme {
  ad: string;
  yogunluk: number; // kg/dm³ (g/cm³)
}

/** Hesaplama sonucu */
export interface MassInfoSonuc {
  hacim: {
    mm3: number;
    cm3: number;
    dm3: number;
  };
  yuzeyAlani: {
    mm2: number;
    cm2: number;
  };
  kutleMerkezi: {
    x: number;
    y: number;
    z: number;
  };
  agirlik: {
    gram: number;
    kg: number;
  };
  boundingBox: {
    min: THREE.Vector3;
    max: THREE.Vector3;
    boyutlar: { x: number; y: number; z: number };
  };
  ucgenSayisi: number;
  malzeme: string;
}

/** Öntanımlı malzemeler */
export const MALZEMELER: Malzeme[] = [
  { ad: 'Alüminyum', yogunluk: 2.71 },
  { ad: 'Çelik', yogunluk: 7.85 },
  { ad: 'Paslanmaz Çelik', yogunluk: 8.00 },
  { ad: 'Bakır', yogunluk: 8.96 },
  { ad: 'Pirinç', yogunluk: 8.50 },
  { ad: 'Bronz', yogunluk: 8.80 },
  { ad: 'Titanyum', yogunluk: 4.51 },
  { ad: 'PLA Plastik', yogunluk: 1.25 },
  { ad: 'ABS Plastik', yogunluk: 1.05 },
  { ad: 'Naylon', yogunluk: 1.14 },
  { ad: 'Cam', yogunluk: 2.50 },
  { ad: 'Ahşap (Meşe)', yogunluk: 0.75 },
  { ad: 'Özel', yogunluk: 1.00 },
];

export class FikoMassInfo {
  private meshler: THREE.Mesh[] = [];
  private malzeme: Malzeme = MALZEMELER[0]; // Varsayılan: Alüminyum

  // Pre-allocated vectors for performance
  private readonly _v0 = new THREE.Vector3();
  private readonly _v1 = new THREE.Vector3();
  private readonly _v2 = new THREE.Vector3();
  private readonly _cross = new THREE.Vector3();
  private readonly _edge1 = new THREE.Vector3();
  private readonly _edge2 = new THREE.Vector3();
  private readonly _centroid = new THREE.Vector3();

  constructor() {}

  /**
   * Malzeme ayarla
   */
  malzemeAyarla(malzeme: Malzeme): void {
    this.malzeme = malzeme;
  }

  /**
   * Özel yoğunluk ayarla
   */
  yogunlukAyarla(yogunluk: number): void {
    this.malzeme = { ad: 'Özel', yogunluk };
  }

  /**
   * Sahne veya Object3D içindeki tüm mesh'leri topla
   */
  meshTara(root: THREE.Object3D): THREE.Mesh[] {
    this.meshler = [];
    root.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.geometry && obj.visible) {
        this.meshler.push(obj);
      }
    });
    return this.meshler;
  }

  /**
   * Tek mesh için hesaplama yap
   */
  hesaplaMesh(mesh: THREE.Mesh): MassInfoSonuc | null {
    if (!mesh.geometry) return null;

    const geometry = mesh.geometry;
    const positionAttr = geometry.getAttribute('position');
    if (!positionAttr) return null;

    // Geometry'yi world koordinatlarına dönüştür
    mesh.updateMatrixWorld(true);
    const matrixWorld = mesh.matrixWorld;

    let hacim = 0;
    let yuzeyAlani = 0;
    let centroidX = 0, centroidY = 0, centroidZ = 0;
    let ucgenSayisi = 0;

    const bbox = new THREE.Box3();

    // Index'li geometry kontrolü
    const indices = geometry.index;
    const isIndexed = indices !== null;
    const count = isIndexed ? indices.count : positionAttr.count;

    for (let i = 0; i < count; i += 3) {
      // Üçgen vertex indeksleri
      const i0 = isIndexed ? indices.getX(i) : i;
      const i1 = isIndexed ? indices.getX(i + 1) : i + 1;
      const i2 = isIndexed ? indices.getX(i + 2) : i + 2;

      // Vertex pozisyonlarını al ve world koordinatına dönüştür
      this._v0.fromBufferAttribute(positionAttr, i0).applyMatrix4(matrixWorld);
      this._v1.fromBufferAttribute(positionAttr, i1).applyMatrix4(matrixWorld);
      this._v2.fromBufferAttribute(positionAttr, i2).applyMatrix4(matrixWorld);

      // BoundingBox güncelle
      bbox.expandByPoint(this._v0);
      bbox.expandByPoint(this._v1);
      bbox.expandByPoint(this._v2);

      // Hacim: Signed Tetrahedron Volume (origin referanslı)
      // V = (v0 · (v1 × v2)) / 6
      this._cross.crossVectors(this._v1, this._v2);
      const tetVolume = this._v0.dot(this._cross) / 6.0;
      hacim += tetVolume;

      // Yüzey Alanı: Üçgen alanı = ||(v1-v0) × (v2-v0)|| / 2
      this._edge1.subVectors(this._v1, this._v0);
      this._edge2.subVectors(this._v2, this._v0);
      this._cross.crossVectors(this._edge1, this._edge2);
      const ucgenAlani = this._cross.length() / 2.0;
      yuzeyAlani += ucgenAlani;

      // Kütle merkezi: Her üçgenin centroid'i × alanı (ağırlıklı ortalama)
      const cx = (this._v0.x + this._v1.x + this._v2.x) / 3;
      const cy = (this._v0.y + this._v1.y + this._v2.y) / 3;
      const cz = (this._v0.z + this._v1.z + this._v2.z) / 3;
      centroidX += cx * ucgenAlani;
      centroidY += cy * ucgenAlani;
      centroidZ += cz * ucgenAlani;

      ucgenSayisi++;
    }

    // Hacim negatifse düzelt (normal yönü ters olabilir)
    hacim = Math.abs(hacim);

    // Kütle merkezi: Ağırlıklı ortalama
    if (yuzeyAlani > 0) {
      centroidX /= yuzeyAlani;
      centroidY /= yuzeyAlani;
      centroidZ /= yuzeyAlani;
    }

    // Birim dönüşümleri (mm varsayılan)
    const hacimMm3 = hacim;
    const hacimCm3 = hacim / 1000; // mm³ → cm³
    const hacimDm3 = hacim / 1000000; // mm³ → dm³

    const alanMm2 = yuzeyAlani;
    const alanCm2 = yuzeyAlani / 100; // mm² → cm²

    // Ağırlık: kg = dm³ × kg/dm³
    const agirlikKg = hacimDm3 * this.malzeme.yogunluk;
    const agirlikGram = agirlikKg * 1000;

    // BoundingBox boyutları
    const boyutlar = new THREE.Vector3();
    bbox.getSize(boyutlar);

    return {
      hacim: {
        mm3: hacimMm3,
        cm3: hacimCm3,
        dm3: hacimDm3,
      },
      yuzeyAlani: {
        mm2: alanMm2,
        cm2: alanCm2,
      },
      kutleMerkezi: {
        x: centroidX,
        y: centroidY,
        z: centroidZ,
      },
      agirlik: {
        gram: agirlikGram,
        kg: agirlikKg,
      },
      boundingBox: {
        min: bbox.min.clone(),
        max: bbox.max.clone(),
        boyutlar: { x: boyutlar.x, y: boyutlar.y, z: boyutlar.z },
      },
      ucgenSayisi,
      malzeme: this.malzeme.ad,
    };
  }

  /**
   * Aktif modelleme gövdesini ölç (varsa govdeMesh + katı makro mesh'leri); yoksa tüm sahne.
   * Maliyet/teklif modülleri için: marker/ölçü/yardımcı geometri bbox'ı şişirmesin.
   */
  hesaplaModel(modelleme: any, sahne: THREE.Object3D | null): MassInfoSonuc | null {
    const meshler: THREE.Mesh[] = [];
    if (modelleme?.govdeMesh?.geometry) meshler.push(modelleme.govdeMesh);
    const makrolar = modelleme?.makroMeshler;
    if (Array.isArray(makrolar)) for (const mm of makrolar) if (mm?.geometry && mm.visible) meshler.push(mm);
    if (meshler.length) {
      const grup = new THREE.Group();
      for (const mm of meshler) grup.children.push(mm);
      const r = this.hesaplaToplam(grup);
      grup.children.length = 0;   // mesh'leri gerçek parent'ından koparmadan geçici grubu boşalt
      return r;
    }
    return sahne ? this.hesaplaToplam(sahne) : null;
  }

  /**
   * Tüm mesh'ler için toplam hesaplama
   */
  hesaplaToplam(root: THREE.Object3D): MassInfoSonuc | null {
    this.meshTara(root);

    if (this.meshler.length === 0) return null;

    let toplamHacim = 0;
    let toplamAlan = 0;
    let toplamCentroidX = 0, toplamCentroidY = 0, toplamCentroidZ = 0;
    let toplamUcgen = 0;

    const toplamBbox = new THREE.Box3();

    for (const mesh of this.meshler) {
      const sonuc = this.hesaplaMesh(mesh);
      if (!sonuc) continue;

      toplamHacim += sonuc.hacim.mm3;
      toplamAlan += sonuc.yuzeyAlani.mm2;

      // Ağırlıklı centroid (hacim bazlı)
      toplamCentroidX += sonuc.kutleMerkezi.x * sonuc.hacim.mm3;
      toplamCentroidY += sonuc.kutleMerkezi.y * sonuc.hacim.mm3;
      toplamCentroidZ += sonuc.kutleMerkezi.z * sonuc.hacim.mm3;

      toplamUcgen += sonuc.ucgenSayisi;

      // BoundingBox birleştir
      toplamBbox.expandByPoint(sonuc.boundingBox.min);
      toplamBbox.expandByPoint(sonuc.boundingBox.max);
    }

    // Ağırlıklı kütle merkezi
    if (toplamHacim > 0) {
      toplamCentroidX /= toplamHacim;
      toplamCentroidY /= toplamHacim;
      toplamCentroidZ /= toplamHacim;
    }

    // Birim dönüşümleri
    const hacimCm3 = toplamHacim / 1000;
    const hacimDm3 = toplamHacim / 1000000;
    const alanCm2 = toplamAlan / 100;

    // Ağırlık
    const agirlikKg = hacimDm3 * this.malzeme.yogunluk;
    const agirlikGram = agirlikKg * 1000;

    // BoundingBox boyutları
    const boyutlar = new THREE.Vector3();
    toplamBbox.getSize(boyutlar);

    return {
      hacim: {
        mm3: toplamHacim,
        cm3: hacimCm3,
        dm3: hacimDm3,
      },
      yuzeyAlani: {
        mm2: toplamAlan,
        cm2: alanCm2,
      },
      kutleMerkezi: {
        x: toplamCentroidX,
        y: toplamCentroidY,
        z: toplamCentroidZ,
      },
      agirlik: {
        gram: agirlikGram,
        kg: agirlikKg,
      },
      boundingBox: {
        min: toplamBbox.min.clone(),
        max: toplamBbox.max.clone(),
        boyutlar: { x: boyutlar.x, y: boyutlar.y, z: boyutlar.z },
      },
      ucgenSayisi: toplamUcgen,
      malzeme: this.malzeme.ad,
    };
  }

  /**
   * Kütle merkezi marker'ı oluştur (3D sahneye eklenecek)
   */
  kutleMerkeziMarkerOlustur(konum: THREE.Vector3, boyut: number = 5): THREE.Group {
    const grup = new THREE.Group();
    grup.name = 'FikoKutleMerkeziMarker';

    // Merkez küre (kırmızı)
    const kureGeo = new THREE.SphereGeometry(boyut, 16, 16);
    const kureMat = new THREE.MeshBasicMaterial({
      color: 0xff3333,
      transparent: true,
      opacity: 0.8
    });
    const kure = new THREE.Mesh(kureGeo, kureMat);
    grup.add(kure);

    // Çapraz çizgiler (X, Y, Z eksenleri)
    const cizgiMat = new THREE.LineBasicMaterial({ color: 0xffff00 });
    const cizgiBoyut = boyut * 3;

    // X ekseni
    const xGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-cizgiBoyut, 0, 0),
      new THREE.Vector3(cizgiBoyut, 0, 0)
    ]);
    grup.add(new THREE.Line(xGeo, cizgiMat));

    // Y ekseni
    const yGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, -cizgiBoyut, 0),
      new THREE.Vector3(0, cizgiBoyut, 0)
    ]);
    grup.add(new THREE.Line(yGeo, cizgiMat));

    // Z ekseni
    const zGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, -cizgiBoyut),
      new THREE.Vector3(0, 0, cizgiBoyut)
    ]);
    grup.add(new THREE.Line(zGeo, cizgiMat));

    // Pozisyon ayarla
    grup.position.copy(konum);

    return grup;
  }
}
